import { FormEvent, useEffect, useMemo, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { isConfigured, supabase } from "./lib/supabase";
import Launch from "./Launch";
import type {
  CampaignRow,
  DashboardStats,
  LaunchOffer,
  WaitlistRow,
} from "./types";

type View = "overview" | "waitlist" | "messages" | "launch" | "safety";
type Segment = "ALL" | "HOT" | "WARM" | "COLD" | "PENDING";
const nav: Array<{ id: View; label: string; icon: string }> = [
  { id: "overview", label: "خانه", icon: "⌂" },
  { id: "waitlist", label: "آدم‌ها", icon: "◎" },
  { id: "messages", label: "پیام", icon: "↗" },
  { id: "launch", label: "Launch", icon: "◉" },
  { id: "safety", label: "امنیت", icon: "◇" },
];
const emptyStats: DashboardStats = {
  people: 0,
  waitlist: 0,
  hot: 0,
  warm: 0,
  cold: 0,
  unqualified: 0,
  sentMessages: 0,
  launchSent: 0,
  launchBlocked: 0,
  checkoutStarted: 0,
  iranSelected: 0,
  internationalSelected: 0,
  accessHandoffs: 0,
  paidOrders: 0,
  backupStatus: "NOT_CONFIGURED",
};
const fa = (n: number) => new Intl.NumberFormat("fa-IR").format(n);
const date = (v?: string | null) =>
  v
    ? new Intl.DateTimeFormat("fa-IR", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(v))
    : "—";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError("");
    const result = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (result.error) setError("ایمیل یا رمز ورود درست نیست.");
  }
  return (
    <main className="login-shell">
      <section className="login-card">
        <div className="brand-mark">SH</div>
        <p className="eyebrow">PRIVATE CONTROL ROOM</p>
        <h1>همه‌چیز، همین‌جا.</h1>
        <p className="muted">
          Waitlist، پیام‌ها و وضعیت Launch پشت یک ورود امن.
        </p>
        {!isConfigured && (
          <div className="notice">اتصال پنل هنوز تنظیم نشده.</div>
        )}
        <form onSubmit={submit}>
          <label>
            ایمیل
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>
          <label>
            رمز ورود
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>
          {error && <p className="error">{error}</p>}
          <button className="primary" disabled={loading || !isConfigured}>
            {loading ? "دارم وارد می‌شم…" : "ورود به Control Room"}
          </button>
        </form>
      </section>
    </main>
  );
}

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [view, setView] = useState<View>("overview");
  const [stats, setStats] = useState(emptyStats);
  const [waitlist, setWaitlist] = useState<WaitlistRow[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [launchOffer, setLaunchOffer] = useState<LaunchOffer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data } = supabase.auth.onAuthStateChange((_e, next) =>
      setSession(next),
    );
    return () => data.subscription.unsubscribe();
  }, []);
  useEffect(() => {
    if (session) void load();
    else setLoading(false);
  }, [session]);
  async function load() {
    setLoading(true);
    setError("");
    const base = () =>
      supabase
        .from("waitlist_entries")
        .select("id", { count: "exact", head: true })
        .eq("status", "WAITLIST");
    const [
      people,
      total,
      hot,
      warm,
      cold,
      pending,
      sent,
      backup,
      rows,
      campaignRows,
      offerRow,
    ] = await Promise.all([
      supabase
        .from("people")
        .select("id", { count: "exact", head: true })
        .is("deleted_at", null),
      base(),
      base().eq("lead_temperature", "HOT"),
      base().eq("lead_temperature", "WARM"),
      base().eq("lead_temperature", "COLD"),
      base().is("lead_temperature", null),
      supabase
        .from("campaign_recipients")
        .select("id", { count: "exact", head: true })
        .eq("status", "SENT"),
      supabase
        .from("backup_runs")
        .select("status")
        .order("started_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabase
        .from("waitlist_entries")
        .select(
          "id,status,joined_at,lead_temperature,qualification_response,people(first_name,last_name,preferred_name,contact_points(kind,value),telegram_identities(username,bot_instances(code))),products(name),lead_sources(code)",
        )
        .order("joined_at", { ascending: false })
        .limit(1000),
      supabase
        .from("campaigns")
        .select(
          "id,name,status,scheduled_at,created_at,message_text,image_url,image_urls,button_text,button_url,segment_filter",
        )
        .order("created_at", { ascending: false })
        .limit(50),
      supabase
        .from("launch_offers")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
    ]);
    const offer = offerRow.data as LaunchOffer | null;
    const campaignData = (campaignRows.data as CampaignRow[]) ?? [];
    const launchCampaign =
      campaignData.find((row) => row.button_url?.includes("buy_rah01")) ?? null;
    const orderCount = (filters?: {
      region?: "IRAN" | "INTERNATIONAL";
      handedOff?: boolean;
      paid?: boolean;
    }) => {
      let query = supabase
        .from("orders")
        .select("id", { count: "exact", head: true });
      if (offer?.id) query = query.eq("offer_id", offer.id);
      else query = query.eq("id", "00000000-0000-0000-0000-000000000000");
      if (filters?.region) query = query.eq("customer_region", filters.region);
      if (filters?.handedOff) query = query.not("handed_off_at", "is", null);
      if (filters?.paid) query = query.eq("status", "PAID");
      return query;
    };
    const recipientCount = (status: "SENT" | "BLOCKED") =>
      launchCampaign
        ? supabase
            .from("campaign_recipients")
            .select("id", { count: "exact", head: true })
            .eq("campaign_id", launchCampaign.id)
            .eq("status", status)
        : Promise.resolve({ count: 0, error: null });
    const [
      launchSent,
      launchBlocked,
      checkoutStarted,
      iranSelected,
      internationalSelected,
      accessHandoffs,
      paidOrders,
    ] = await Promise.all([
      recipientCount("SENT"),
      recipientCount("BLOCKED"),
      orderCount(),
      orderCount({ region: "IRAN" }),
      orderCount({ region: "INTERNATIONAL" }),
      orderCount({ handedOff: true }),
      orderCount({ paid: true }),
    ]);
    if (
      [
        people,
        total,
        hot,
        warm,
        cold,
        pending,
        sent,
        backup,
        rows,
        campaignRows,
        offerRow,
        launchSent,
        launchBlocked,
        checkoutStarted,
        iranSelected,
        internationalSelected,
        accessHandoffs,
        paidOrders,
      ].some((r) => r.error)
    )
      setError("بخشی از داده‌ها باز نشد. دسترسی Owner را بررسی کن.");
    setStats({
      people: people.count ?? 0,
      waitlist: total.count ?? 0,
      hot: hot.count ?? 0,
      warm: warm.count ?? 0,
      cold: cold.count ?? 0,
      unqualified: pending.count ?? 0,
      sentMessages: sent.count ?? 0,
      launchSent: launchSent.count ?? 0,
      launchBlocked: launchBlocked.count ?? 0,
      checkoutStarted: checkoutStarted.count ?? 0,
      iranSelected: iranSelected.count ?? 0,
      internationalSelected: internationalSelected.count ?? 0,
      accessHandoffs: accessHandoffs.count ?? 0,
      paidOrders: paidOrders.count ?? 0,
      backupStatus:
        (backup.data?.status as DashboardStats["backupStatus"]) ??
        "NOT_CONFIGURED",
    });
    setWaitlist((rows.data as unknown as WaitlistRow[]) ?? []);
    setCampaigns(campaignData);
    setLaunchOffer(offer);
    setLoading(false);
  }
  if (!session) return <Login />;
  const current = nav.find((i) => i.id === view)!;
  return (
    <div className="app-shell">
      <aside>
        <div className="rail-head">
          <div className="brand-mark small">SH</div>
          <div>
            <strong>Sinpe House</strong>
            <span>Control Room</span>
          </div>
        </div>
        <nav>
          {nav.map((i) => (
            <button
              key={i.id}
              className={view === i.id ? "active" : ""}
              onClick={() => setView(i.id)}
            >
              <b>{i.icon}</b>
              <span>{i.label}</span>
            </button>
          ))}
        </nav>
        <div className="rail-foot">
          <span className="status-dot" />
          Online
          <button onClick={() => supabase.auth.signOut()}>خروج امن</button>
        </div>
      </aside>
      <main className="content">
        <header>
          <div>
            <p className="eyebrow">LIVE / RAH 01</p>
            <h1>{current.label}</h1>
          </div>
          <button className="quiet" onClick={load}>
            ↻ تازه‌سازی
          </button>
        </header>
        {error && <div className="notice">{error}</div>}
        {loading ? (
          <div className="loading">دارم وضعیت زنده رو می‌گیرم…</div>
        ) : view === "overview" ? (
          <Overview stats={stats} rows={waitlist} />
        ) : view === "waitlist" ? (
          <Waitlist rows={waitlist} />
        ) : view === "messages" ? (
          <Campaigns
            rows={campaigns}
            leads={waitlist}
            session={session}
            reload={load}
          />
        ) : view === "launch" ? (
          <Launch offer={launchOffer} reload={load} />
        ) : (
          <Safety stats={stats} />
        )}
      </main>
    </div>
  );
}

const nameOf = (r: WaitlistRow) =>
  r.people?.preferred_name ||
  [r.people?.first_name, r.people?.last_name].filter(Boolean).join(" ") ||
  "بدون نام";
const contact = (r: WaitlistRow, k: string) =>
  r.people?.contact_points?.find((c) => c.kind === k)?.value;
const telegramUsername = (r: WaitlistRow) =>
  r.people?.telegram_identities?.find(
    (identity) => identity.bot_instances?.code === "SINPE_HOUSE",
  )?.username ?? null;
function Temp({ v }: { v: WaitlistRow["lead_temperature"] }) {
  return (
    <span className={`temp ${v?.toLowerCase() ?? "none"}`}>
      {v ?? "PENDING"}
    </span>
  );
}
function Cards({ rows }: { rows: WaitlistRow[] }) {
  return (
    <div className="lead-cards">
      {rows.length ? (
        rows.map((r) => (
          <article className="lead-card" key={r.id}>
            <div className="lead-top">
              <div className="avatar">{nameOf(r)[0]}</div>
              <div>
                <strong>{nameOf(r)}</strong>
                <small>{date(r.joined_at)}</small>
              </div>
              <Temp v={r.lead_temperature} />
            </div>
            <div className="contacts">
              <span>{contact(r, "EMAIL") || "Email ثبت نشده"}</span>
              <span>{contact(r, "PHONE") || "Mobile ثبت نشده"}</span>
            </div>
            <footer>
              <code>{r.lead_sources?.code ?? "telegram_direct"}</code>
              <span>{r.products?.name ?? "RAH 01"}</span>
            </footer>
          </article>
        ))
      ) : (
        <div className="empty">هنوز کسی وارد Waitlist نشده.</div>
      )}
    </div>
  );
}
function Overview({
  stats,
  rows,
}: {
  stats: DashboardStats;
  rows: WaitlistRow[];
}) {
  const percent = (value: number) =>
    stats.launchSent
      ? `${fa(Math.round((value / stats.launchSent) * 100))}٪ از ارسال‌های موفق`
      : "—";
  return (
    <>
      <section className="hero-count">
        <div>
          <span>WAITLIST / RAH 01</span>
          <strong>{fa(stats.waitlist)}</strong>
          <p>نفر منتظر Launch هستند</p>
        </div>
        <div className="temperature">
          <span className="hot">HOT · {fa(stats.hot)}</span>
          <span className="warm">WARM · {fa(stats.warm)}</span>
          <span className="cold">COLD · {fa(stats.cold)}</span>
        </div>
      </section>
      <section className="mini-grid">
        <article>
          <span>Qualification ناقص</span>
          <strong>{fa(stats.unqualified)}</strong>
        </article>
        <article>
          <span>کل ارسال موفق</span>
          <strong>{fa(stats.sentMessages)}</strong>
        </article>
        <article>
          <span>کل آدم‌ها</span>
          <strong>{fa(stats.people)}</strong>
        </article>
      </section>
      <section className="panel funnel">
        <div className="panel-head">
          <div>
            <p className="eyebrow">LIVE FUNNEL</p>
            <h2>قیف لانچ</h2>
          </div>
          <span>آپدیت زنده</span>
        </div>
        <div className="funnel-grid">
          <article>
            <span>ارسال موفق لانچ</span>
            <strong>{fa(stats.launchSent)}</strong>
            <small>
              {stats.launchBlocked
                ? `${fa(stats.launchBlocked)} نفر بات را بلاک کرده‌اند`
                : "تلگرام پیام را پذیرفته"}
            </small>
          </article>
          <article>
            <span>شروع ثبت‌نام</span>
            <strong>{fa(stats.checkoutStarted)}</strong>
            <small>{percent(stats.checkoutStarted)}</small>
          </article>
          <article>
            <span>انتخاب پرداخت</span>
            <strong>
              {fa(stats.iranSelected + stats.internationalSelected)}
            </strong>
            <small>
              ایران {fa(stats.iranSelected)} · خارج{" "}
              {fa(stats.internationalSelected)}
            </small>
          </article>
          <article>
            <span>رفتن برای دسترسی</span>
            <strong>{fa(stats.accessHandoffs)}</strong>
            <small>{percent(stats.accessHandoffs)}</small>
          </article>
          <article className="paid">
            <span>پرداخت تأییدشده</span>
            <strong>{fa(stats.paidOrders)}</strong>
            <small>{percent(stats.paidOrders)}</small>
          </article>
        </div>
        {stats.accessHandoffs > stats.paidOrders && (
          <div className="funnel-note">
            تأییدهای دستی Access Bot هنوز خودکار به House برنمی‌گردند؛ عدد
            پرداخت قطعی ممکن است کمتر از واقعیت دیده شود.
          </div>
        )}
      </section>
      <section className="panel recent">
        <div className="panel-head">
          <h2>آخرین ورودی‌ها</h2>
          <span>Live</span>
        </div>
        <Cards rows={rows.slice(0, 5)} />
      </section>
    </>
  );
}
function Waitlist({ rows }: { rows: WaitlistRow[] }) {
  const [segment, setSegment] = useState<Segment>("ALL");
  const [q, setQ] = useState("");
  const filtered = useMemo(
    () =>
      rows.filter(
        (r) =>
          (segment === "ALL" ||
            (segment === "PENDING"
              ? r.lead_temperature === null
              : r.lead_temperature === segment)) &&
          `${nameOf(r)} ${telegramUsername(r) ?? ""} ${contact(r, "EMAIL") ?? ""} ${contact(r, "PHONE") ?? ""}`
            .toLowerCase()
            .includes(q.toLowerCase()),
      ),
    [rows, segment, q],
  );
  return (
    <>
      <div className="filter">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="اسم، یوزرنیم، ایمیل یا موبایل…"
        />
        <div>
          {(["ALL", "HOT", "WARM", "COLD", "PENDING"] as Segment[]).map(
            (s) => (
              <button
                key={s}
                className={segment === s ? "active" : ""}
                onClick={() => setSegment(s)}
              >
                {s}
              </button>
            ),
          )}
        </div>
      </div>
      <p className="count">{fa(filtered.length)} نفر</p>
      <Cards rows={filtered} />
    </>
  );
}

function Campaigns({
  rows,
  leads,
  session,
  reload,
}: {
  rows: CampaignRow[];
  leads: WaitlistRow[];
  session: Session;
  reload: () => Promise<void>;
}) {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [buttonText, setButtonText] = useState("");
  const [buttonUrl, setButtonUrl] = useState("");
  const [segment, setSegment] = useState<Segment>("ALL");
  const [feedback, setFeedback] = useState("");
  const [busy, setBusy] = useState("");
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const imagePreviews = useMemo(
    () => imageFiles.map((file) => URL.createObjectURL(file)),
    [imageFiles],
  );
  useEffect(
    () => () => {
      imagePreviews.forEach((url) => URL.revokeObjectURL(url));
    },
    [imagePreviews],
  );
  const [selectedMessage, setSelectedMessage] = useState("");
  const [selectedPerson, setSelectedPerson] = useState("");
  const [myProfile, setMyProfile] = useState(
    () => localStorage.getItem("sinpehouse_test_waitlist_id") ?? "",
  );
  const chosenMessage = rows.find((row) => row.id === selectedMessage);
  const chosenPerson = leads.find((lead) => lead.id === selectedPerson);
  const savedMe = leads.find((lead) => lead.id === myProfile);
  function chooseImages(files: File[]) {
    setFeedback("");
    if (!files.length) {
      setImageFiles([]);
      return;
    }
    if (files.length > 10) {
      setFeedback("حداکثر ۱۰ عکس در هر Album قابل ارسال است.");
      return;
    }
    if (
      files.some(
        (file) =>
          !["image/jpeg", "image/png", "image/webp"].includes(file.type),
      )
    ) {
      setFeedback("فقط JPG، PNG یا WebP قابل ارسال است.");
      return;
    }
    if (files.some((file) => file.size > 10 * 1024 * 1024)) {
      setFeedback("حجم هر عکس باید کمتر از ۱۰MB باشد.");
      return;
    }
    setImageFiles(files);
  }
  async function draft(e: FormEvent) {
    e.preventDefault();
    setBusy("draft");
    setFeedback("");
    if (message.length > (imageFiles.length === 1 ? 1024 : 4096)) {
      setFeedback(
        imageFiles.length === 1
          ? "Caption عکس باید حداکثر ۱۰۲۴ کاراکتر باشد."
          : "متن باید حداکثر ۴۰۹۶ کاراکتر باشد.",
      );
      setBusy("");
      return;
    }
    const [{ data: product }, { data: bot }] = await Promise.all([
      supabase.from("products").select("id").eq("code", "RAH_01").single(),
      supabase
        .from("bot_instances")
        .select("id")
        .eq("code", "SINPE_HOUSE")
        .single(),
    ]);
    if (!product || !bot) {
      setFeedback("محصول یا بات پیدا نشد.");
      setBusy("");
      return;
    }
    const imageUrls: string[] = [];
    const uploadedPaths: string[] = [];
    for (const imageFile of imageFiles) {
      const extension =
        imageFile.type === "image/png"
          ? "png"
          : imageFile.type === "image/webp"
            ? "webp"
            : "jpg";
      const uploadedPath = `${session.user.id}/${crypto.randomUUID()}.${extension}`;
      const upload = await supabase.storage
        .from("campaign-media")
        .upload(uploadedPath, imageFile, {
          contentType: imageFile.type,
          cacheControl: "3600",
          upsert: false,
        });
      if (upload.error) {
        if (uploadedPaths.length)
          await supabase.storage.from("campaign-media").remove(uploadedPaths);
        setFeedback(`عکس آپلود نشد: ${upload.error.message}`);
        setBusy("");
        return;
      }
      uploadedPaths.push(uploadedPath);
      imageUrls.push(
        supabase.storage.from("campaign-media").getPublicUrl(uploadedPath).data
          .publicUrl,
      );
    }
    const result = await supabase
      .from("campaigns")
      .insert({
        code: `RAH01_${Date.now()}`,
        name: title,
        product_id: product.id,
        bot_id: bot.id,
        status: "DRAFT",
        message_text: message,
        image_url: imageUrls[0] ?? null,
        image_urls: imageUrls,
        button_text: buttonText || null,
        button_url: buttonUrl || null,
        segment_filter: {
          product_code: "RAH_01",
          waitlist_status: ["WAITLIST", "INVITED", "CHECKOUT"],
          lead_temperature: segment === "ALL" ? [] : [segment],
        },
        created_by: session.user.id,
      });
    setBusy("");
    if (result.error) {
      if (uploadedPaths.length)
        await supabase.storage.from("campaign-media").remove(uploadedPaths);
      setFeedback("Draft ذخیره نشد. دسترسی Owner را بررسی کن.");
      return;
    }
    setTitle("");
    setMessage("");
    setImageFiles([]);
    setButtonText("");
    setButtonUrl("");
    setFeedback("Draft آماده شد. Preview را چک کن.");
    await reload();
  }
  async function send(row: CampaignRow) {
    if (!window.confirm(`پیام «${row.name}» واقعاً ارسال شود؟`)) return;
    setBusy(row.id);
    setFeedback("ارسال شروع شد…");
    const result = await supabase.functions.invoke("admin-campaign", {
      body: { action: "launch", campaign_id: row.id, confirmation: "SEND" },
    });
    setBusy("");
    setFeedback(
      result.error
        ? `ارسال نشد: ${result.error.message}`
        : `Done. ${result.data?.sent ?? 0} پیام ارسال شد.`,
    );
    await reload();
  }
  async function repair(row: CampaignRow) {
    if (
      !window.confirm(
        `دکمه پیام «${row.name}» برای همه دریافت‌کننده‌ها اصلاح شود؟`,
      )
    )
      return;
    setBusy(`repair:${row.id}`);
    setFeedback("دارم دکمه‌های ارسال‌شده را اصلاح می‌کنم…");
    const result = await supabase.functions.invoke("admin-campaign", {
      body: {
        action: "repair_buttons",
        campaign_id: row.id,
        confirmation: "REPAIR_BUTTONS",
      },
    });
    setBusy("");
    setFeedback(
      result.error
        ? `اصلاح نشد: ${result.error.message}`
        : `Done. دکمه ${result.data?.repaired ?? 0} پیام اصلاح شد؛ ${result.data?.failed ?? 0} ناموفق.`,
    );
  }
  async function resume(row: CampaignRow) {
    setBusy(`resume:${row.id}`);
    setFeedback("ادامه ارسال باقی‌مانده‌ها شروع شد…");
    const result = await supabase.functions.invoke("admin-campaign", {
      body: {
        action: "resume",
        campaign_id: row.id,
        confirmation: "RESUME",
      },
    });
    setBusy("");
    setFeedback(
      result.error
        ? `ادامه ارسال انجام نشد: ${result.error.message}`
        : `صف ادامه پیدا کرد؛ ${result.data?.sent ?? 0} ارسال جدید در این مرحله.`,
    );
    await reload();
  }
  function rememberMe() {
    if (!chosenPerson) {
      setFeedback("اول پروفایل خودت را از لیست انتخاب کن.");
      return;
    }
    localStorage.setItem("sinpehouse_test_waitlist_id", chosenPerson.id);
    setMyProfile(chosenPerson.id);
    setFeedback(`${nameOf(chosenPerson)} به‌عنوان پروفایل تست ذخیره شد.`);
  }
  async function sendOne(waitlistId: string, mode: "self" | "selected") {
    const target = leads.find((lead) => lead.id === waitlistId);
    if (!chosenMessage) {
      setFeedback("اول یک پیام را انتخاب کن.");
      return;
    }
    if (!target) {
      setFeedback(
        mode === "self"
          ? "اول پروفایل خودت را انتخاب و ذخیره کن."
          : "اول یک نفر را انتخاب کن.",
      );
      return;
    }
    if (
      mode === "selected" &&
      !window.confirm(
        `پیام «${chosenMessage.name}» فقط برای ${nameOf(target)} ارسال شود؟`,
      )
    )
      return;
    setBusy(`direct:${mode}`);
    setFeedback(
      mode === "self"
        ? "دارم نسخه تست را برای خودت می‌فرستم…"
        : `دارم فقط برای ${nameOf(target)} می‌فرستم…`,
    );
    const result = await supabase.functions.invoke("admin-campaign", {
      body: {
        action: "send_direct",
        campaign_id: chosenMessage.id,
        waitlist_id: target.id,
        confirmation: "SEND_ONE",
      },
    });
    setBusy("");
    setFeedback(
      result.error
        ? `ارسال نشد: ${result.error.message}`
        : `Done. پیام فقط برای ${nameOf(target)} ارسال شد.`,
    );
    if (!result.error) await reload();
  }
  return (
    <section className="campaign-layout">
      <form className="panel composer" onSubmit={draft}>
        <p className="eyebrow">NEW MESSAGE</p>
        <h2>پیامت رو بساز</h2>
        <p className="muted">Draft، Preview، بعد تأیید واقعی.</p>
        <label>
          اسم داخلی
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Launch RAH 01"
            required
          />
        </label>
        <label>
          مخاطب
          <select
            value={segment}
            onChange={(e) => setSegment(e.target.value as Segment)}
          >
            <option value="ALL">همه Waitlist</option>
            <option value="HOT">فقط HOT</option>
            <option value="WARM">فقط WARM</option>
            <option value="COLD">فقط COLD</option>
            <option value="PENDING">فقط PENDING / ناقص</option>
          </select>
        </label>
        <label>
          متن
          <textarea
            rows={7}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={"Let’s go.\nثبت‌نام باز شد…"}
            required
          />
        </label>
        <label className="media-upload">
          عکس یا Album <small>اختیاری · حداکثر ۱۰ عکس · هرکدام تا ۱۰MB</small>
          <input
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => chooseImages(Array.from(e.target.files ?? []))}
          />
          {imageFiles.length > 0 && (
            <span className="selected-media">
              {fa(imageFiles.length)} عکس انتخاب شد
              <button type="button" onClick={() => setImageFiles([])}>
                حذف همه
              </button>
            </span>
          )}
        </label>
        <div className="pair">
          <label>
            متن دکمه
            <input
              value={buttonText}
              onChange={(e) => setButtonText(e.target.value)}
            />
          </label>
          <label>
            لینک
            <input
              type="url"
              value={buttonUrl}
              onChange={(e) => setButtonUrl(e.target.value)}
              placeholder="https://"
            />
          </label>
        </div>
        <div className="preview">
          <span>Sinpehouse</span>
          {imagePreviews.length > 0 && (
            <div className="preview-album">
              {imagePreviews.map((url, index) => (
                <img key={url} src={url} alt={`Preview ${index + 1}`} />
              ))}
            </div>
          )}
          <p>{message || "Preview پیام اینجا دیده می‌شود."}</p>
          {buttonText && <button type="button">{buttonText}</button>}
        </div>
        {feedback && <div className="notice">{feedback}</div>}
        <button className="primary" disabled={busy === "draft"}>
          {busy === "draft" ? "در حال ذخیره…" : "ذخیره Draft"}
        </button>
      </form>
      <div className="campaign-list">
        <section className="panel direct-send">
          <h2>اول برای خودت تست کن</h2>
          <p>
            یک پیام و یک نفر را انتخاب کن. Broadcast فقط از دکمه‌های کارت‌های
            پایین انجام می‌شود.
          </p>
          <label>
            پیام
            <select
              value={selectedMessage}
              onChange={(e) => setSelectedMessage(e.target.value)}
            >
              <option value="">انتخاب پیام…</option>
              {rows.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name} · {row.status}
                </option>
              ))}
            </select>
          </label>
          <label>
            آدم
            <select
              value={selectedPerson}
              onChange={(e) => setSelectedPerson(e.target.value)}
            >
              <option value="">انتخاب از Waitlist…</option>
              {leads.map((lead) => (
                <option key={lead.id} value={lead.id}>
                  {nameOf(lead)}
                  {telegramUsername(lead)
                    ? ` · @${telegramUsername(lead)}`
                    : ""}{" "}
                  ·{" "}
                  {contact(lead, "EMAIL") ??
                    lead.lead_temperature ??
                    "بدون ایمیل"}
                </option>
              ))}
            </select>
          </label>
          <div className="identity-row">
            <button
              type="button"
              className="quiet"
              onClick={rememberMe}
              disabled={!chosenPerson}
            >
              این پروفایل من است
            </button>
            <span>
              {savedMe
                ? `پروفایل تست: ${nameOf(savedMe)}`
                : "هنوز پروفایل تست انتخاب نشده"}
            </span>
          </div>
          <div className="direct-actions">
            <button
              type="button"
              className="test-send"
              disabled={
                !chosenMessage || !savedMe || busy.startsWith("direct:")
              }
              onClick={() => void sendOne(myProfile, "self")}
            >
              {busy === "direct:self" ? "در حال ارسال…" : "Send test to me"}
            </button>
            <button
              type="button"
              className="single-send"
              disabled={
                !chosenMessage || !chosenPerson || busy.startsWith("direct:")
              }
              onClick={() => void sendOne(selectedPerson, "selected")}
            >
              {busy === "direct:selected"
                ? "در حال ارسال…"
                : "ارسال فقط به فرد انتخاب‌شده"}
            </button>
          </div>
        </section>
        <div className="panel-head">
          <h2>پیام‌ها</h2>
          <span>{fa(rows.length)} مورد</span>
        </div>
        {rows.length ? (
          rows.map((r) => (
            <article className="campaign-card" key={r.id}>
              <div>
                <strong>{r.name}</strong>
                <small>{date(r.created_at)}</small>
              </div>
              <span className="badge">{r.status}</span>
              {r.image_url && (
                <img className="campaign-media" src={r.image_url} alt="" />
              )}
              <p>{r.message_text}</p>
              <footer>
                <code>{r.segment_filter?.lead_temperature?.[0] ?? "ALL"}</code>
                {r.status === "DRAFT" && (
                  <button
                    className="send"
                    disabled={busy === r.id}
                    onClick={() => send(r)}
                  >
                    {busy === r.id ? "در حال ارسال…" : "Preview و ارسال گروهی"}
                  </button>
                )}
                {r.status === "COMPLETED" &&
                  r.button_url?.includes("t.me/sinpehousebot?start=") && (
                    <button
                      className="send"
                      disabled={busy === `repair:${r.id}`}
                      onClick={() => repair(r)}
                    >
                      {busy === `repair:${r.id}`
                        ? "در حال اصلاح…"
                        : "اصلاح دکمه‌های ارسال‌شده"}
                    </button>
                  )}
                {["SCHEDULED", "SENDING"].includes(r.status) && (
                  <button
                    className="send"
                    disabled={busy === `resume:${r.id}`}
                    onClick={() => void resume(r)}
                  >
                    {busy === `resume:${r.id}`
                      ? "در حال ادامه…"
                      : "ادامه ارسال باقی‌مانده‌ها"}
                  </button>
                )}
              </footer>
            </article>
          ))
        ) : (
          <div className="empty">هنوز پیامی نساختی.</div>
        )}
      </div>
    </section>
  );
}
function Safety({ stats }: { stats: DashboardStats }) {
  return (
    <section className="panel safety">
      <p className="eyebrow">SYSTEM STATUS</p>
      <h2>داده‌ها سر جاشه.</h2>
      <div className="safety-state">
        <span className="status-dot" />
        <strong>{stats.backupStatus}</strong>
      </div>
      <p>
        بکاپ Google Drive هنوز باید متصل شود. هیچ پیامی بدون Draft و تأیید نهایی
        ارسال نمی‌شود.
      </p>
      <button className="quiet" onClick={() => supabase.auth.signOut()}>
        خروج امن
      </button>
    </section>
  );
}
